<template>
  <div>
    <div>
      <InputText
        v-model="query"
        autocomplete="off"
        :class="{ disabled: disabled }"
        :clearable="!loading"
        data-testid="input"
        :disabled="disabled"
        new-design
        :placeholder="placeholder"
        @blur="onTextBlur"
        @click.stop="onTextFocus"
        @focus="onTextFocus"
        @keydown.down.stop="incrementSearchIndex"
        @keydown.enter.stop="onKeyEnterOrTab"
        @keydown.tab.stop="onKeyEnterOrTab"
        @keydown.up.stop="decrementSearchIndex"
      >
        <template #prepend>
          <BaseIcon
            class="tw:text-xl"
            icon="search"
          />
        </template>
        <template #append>
          <div
            v-if="loading"
            class="tw:px-2"
          >
            <QSpinner />
          </div>
        </template>
      </InputText>
    </div>
    <Popper
      v-if="shouldShowDropdown"
      v-model="dropdownVisible"
      data-testid="popper"
      placement="bottom-start"
      same-width
    >
      <div
        class="tw:rounded-sm tw:border tw:border-solid tw:border-denim-300 tw:bg-white"
        data-testid="dropdown"
        @mousedown.prevent
      >
        <slot name="beforeOptions" />
        <div
          v-if="!hasItems"
          class="tw:p-2"
        >
          <i18n-t
            v-if="!filteredItems.length && query.length >= minimumCharacters"
            class="no-results"
            keypath="filters_autocomplete.no_results_found_for"
            scope="global"
            tag="div"
          >
            <template #search>
              <em>{{ query }}</em>
            </template>
          </i18n-t>
          <div v-else-if="query.length > 0 && query.length < minimumCharacters">
            {{
              getLocaleText("filters_autocomplete.type_at_least", {
                minimumCharacters,
              })
            }}
          </div>
        </div>
        <div
          v-else
          class="list tw:max-h-[260px] tw:overflow-auto"
          data-testid="list"
        >
          <QInfiniteScroll
            ref="infiniteScroll"
            :offset="pageSize"
            scroll-target=".list"
            @load="loadMoreItems"
          >
            <div
              v-for="(item, index) in filteredItems"
              :key="index"
              ref="itemElements"
              class="tw:cursor-pointer tw:select-none tw:p-2 tw:hover:bg-denim-100"
              :class="{ 'tw:bg-denim-100': itemIndex === index }"
              data-cy="item"
              data-testid="item"
              @click="selectItem(item)"
              @mousedown="preventDropdownClose"
              @mouseup="allowDropdownClose"
            >
              <slot
                name="option"
                :option="item"
              >
                <EllipseContent>
                  {{ item.name }}
                </EllipseContent>
              </slot>
            </div>

            <template #loading>
              <div class="tw:pa-2 tw:flex tw:justify-center">
                <QSpinnerDots size="2em" />
              </div>
            </template>
          </QInfiniteScroll>
        </div>
      </div>
    </Popper>
  </div>
</template>

<script lang="ts">
export interface SourceFiltersSelectorFilterResponseItem {
  id: number | string;
  name: string;
}
</script>

<script
  setup
  lang="ts"
  generic="T extends SourceFiltersSelectorFilterResponseItem"
>
import { debounce } from "lodash-es";
import { QInfiniteScroll, QSpinner, QSpinnerDots } from "quasar";
import { type Ref, computed, nextTick, ref, watch } from "vue";

import { getLocaleText } from "shared/boot/i18n";
import { BaseIcon, InputText } from "shared/components/base";
import EllipseContent from "shared/components/base/EllipseContent.vue";
import Popper from "shared/components/base/Popper.vue";

export interface SourceFiltersSelectorFilterResponse<T> {
  items: T[];
  pages: number;
}

export interface SourceFiltersSelectorProps<T> {
  minimumCharacters?: number;
  disabled?: boolean;
  placeholder?: string;
  keepExpanded?: boolean;
  pageSize?: number;
  filter: (index: number) => Promise<SourceFiltersSelectorFilterResponse<T>>;
}

const props = withDefaults(defineProps<SourceFiltersSelectorProps<T>>(), {
  minimumCharacters: 3,
  placeholder: "",
  pageSize: 40,
});

const modelValue = defineModel<T["id"][]>({
  default: () => [],
});

const emit = defineEmits(["query", "item", "visible"]);

const loading = ref(false);
const query = ref("");
const items = ref<T[]>([]) as Ref<T[]>;
const dropdownVisible = ref(false);
const allowDropdownToClose = ref(true);
const itemIndex = ref<number>(0);
const infiniteScroll = ref<InstanceType<typeof QInfiniteScroll> | undefined>();
const itemElements = ref<HTMLDivElement[]>([]);
const page = ref(1);

const filteredItems = computed<T[]>(() =>
  items.value.filter((item) => !modelValue.value.includes(item.id))
);

const hasItems = computed<boolean>(() => Boolean(filteredItems.value.length));

const shouldShowDropdown = computed<boolean>(() =>
  Boolean(dropdownVisible.value && (query.value || hasItems.value))
);

function showDropdown(): void {
  dropdownVisible.value = true;
}

async function loadItems(index: number): Promise<void> {
  loading.value = true;
  const result = await props.filter(index);

  if (page.value >= result.pages) {
    infiniteScroll.value?.stop();
  }

  items.value.push(...result.items);
  page.value += 1;
  loading.value = false;
}

const debouncedSearch = debounce(() => {
  loadItems(0);
}, 300);

function startSearch(): void {
  page.value = 1;
  items.value = [];
  loading.value = true;
  debouncedSearch();
}

const loadMoreItems: QInfiniteScroll["onLoad"] = async (
  index: number,
  done: () => void
) => {
  if (loading.value) {
    done();

    return;
  }

  await loadItems(index);

  done();
};

function hideDropdown(): void {
  dropdownVisible.value = false;
  itemIndex.value = -1;
  query.value = "";
  page.value = 1;
}

function selectItem(item: SourceFiltersSelectorFilterResponseItem): void {
  modelValue.value = [...modelValue.value, item.id];
  emit("item", item);

  if (!props.keepExpanded) {
    hideDropdown();
  }
}

function onTextBlur(): void {
  if (!allowDropdownToClose.value) return;

  hideDropdown();
}

function onTextFocus(): void {
  startSearch();
  showDropdown();
}

type ItemElement = HTMLDivElement & {
  scrollIntoViewIfNeeded?: (centerIfNeeded: boolean) => void;
};

function scrollOptionIntoView(): void {
  nextTick(() => {
    const el: ItemElement = itemElements.value[itemIndex.value];

    if (!el) return;

    if (el.scrollIntoViewIfNeeded !== undefined) {
      el.scrollIntoViewIfNeeded(false);
    } else if (el.scrollIntoView !== undefined) {
      el.scrollIntoView({ block: "nearest" });
    }
  });
}

function decrementSearchIndex(): void {
  if (itemIndex.value - 1 >= 0) {
    itemIndex.value -= 1;
    scrollOptionIntoView();
  }
}

function incrementSearchIndex(): void {
  if (itemIndex.value + 1 < filteredItems.value.length) {
    itemIndex.value += 1;
    scrollOptionIntoView();
  }
}

function onKeyEnterOrTab(): void {
  if (itemIndex.value >= 0) {
    const item = filteredItems.value[itemIndex.value];

    if (item) {
      selectItem(item);
    }
  }

  showDropdown();
}

function preventDropdownClose(): void {
  allowDropdownToClose.value = false;
}

function allowDropdownClose(): void {
  allowDropdownToClose.value = true;
}

watch(query, () => {
  emit("query", query.value);

  if (query.value.length >= props.minimumCharacters) {
    startSearch();
    showDropdown();
  } else {
    page.value = 1;
    items.value = [];
    itemIndex.value = -1;
  }
});

watch(dropdownVisible, () => {
  emit("visible", dropdownVisible.value);
});

defineExpose({ search: startSearch });
</script>
